import { useState, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'

/**
 * Silently acquires a Microsoft Graph token and fetches the signed-in user's
 * profile photo. Returns a blob object URL, or null if unavailable (no photo
 * set, or silent token acquisition failed). Revokes the blob URL on unmount.
 */
export function useGraphPhoto(): string | null {
  const { instance, accounts } = useMsal()
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    const account = accounts[0]
    if (!account) return

    let objectUrl: string | null = null
    let cancelled = false

    void (async () => {
      try {
        const result = await instance.acquireTokenSilent({
          scopes: ['User.Read'],
          account,
        })

        const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${result.accessToken}` },
        })

        if (!response.ok || cancelled) return

        const blob = await response.blob()
        if (cancelled) return

        objectUrl = URL.createObjectURL(blob)
        setPhotoUrl(objectUrl)
      } catch {
        // Photo not available or token acquisition requires interaction — show initials
      }
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [instance, accounts])

  return photoUrl
}
